import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';

// Recursively lower-case all object keys.  Defends against PascalCase
// responses some legacy controllers emit (System.Text.Json default when
// AddJsonOptions hasn't applied the CamelCase naming policy).
function lcKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(lcKeys);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      out[k.toLowerCase()] = lcKeys(val);
    }
    return out;
  }
  return v;
}

// ─────────────────────────────────────────────────────────────────────────
// Settings → Email Alerts
// ─────────────────────────────────────────────────────────────────────────
//   Configures the Server Monitor daily summary email:
//     • Enabled toggle
//     • From address (single)
//     • To addresses (MANY — add/remove rows freely)
//     • SMTP host / port / user / password / SSL
//     • Send hour (0-23, local time)
//
//   Persisted to dbo.AI_EmailAlertSettings.  The daily email worker
//   re-reads on every 5-minute cycle so changes take effect immediately
//   without an API restart.
// ─────────────────────────────────────────────────────────────────────────

interface EmailAlertSettings {
  settingKey: string;
  enabled: boolean;
  fromAddress: string | null;
  toAddresses: string[];
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUser: string | null;
  hasSmtpPassword: boolean;
  useSsl: boolean;
  sendHourLocal: number;
}

export default function EmailAlertsPage() {
  const notify = useNotify();
  // View-then-Edit gate (BO UI Standard v2): the panel opens READ-ONLY; the
  // toolbar Edit button unlocks the controls; Save persists through the EXISTING
  // PUT via the shared useSaveFlow; Cancel discards edits by reloading from the
  // server.  (user request 2026-07-07)
  const gate = useEditGate();
  const ro = gate.readOnly;
  const [s,         setS]         = useState<EmailAlertSettings | null>(null);
  const [pwd,       setPwd]       = useState('');           // new password (empty = keep)
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  /** Persistent load-failure message — shown in the empty state when the
   *  initial fetch never produces settings. Save outcomes go through the
   *  shared notification service. */
  const [errMsg,    setErrMsg]    = useState<string | null>(null);

  // ── Load with a hard 10-second guard.  If the request never settles
  //   (stale auth token, API down, dropped TCP), the page would otherwise
  //   hang on "Loading settings…" forever — instead show a clear error
  //   and a Retry button.  Bumped attempt # invalidates the prior call.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setLoading(true); setErrMsg(null); setS(null);
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      controller.abort();
      setErrMsg('Timed out after 10 s.  The API may be unreachable — check that the server is running and your session is still valid.');
      setLoading(false);
    }, 10_000);

    api.get('/api/v1/settings/email-alerts', { signal: controller.signal })
      .then(res => {
        // Accept either envelope {success,data:{...}} OR direct {...}.
        // Different controllers in this codebase use both shapes; sniff
        // for the settingKey marker.  Case-insensitive — some legacy
        // endpoints PascalCase responses (Data / SettingKey).
        const body: unknown = res.data;
        const calledUrl   = (res.config.baseURL ?? '') + (res.config.url ?? '');
        const contentType = (res.headers as Record<string, string | undefined>)['content-type'] ?? '?';
        // eslint-disable-next-line no-console
        console.log('[email-alerts] called', calledUrl, 'content-type', contentType, 'body', body);

        // If the response is HTML, the request didn't reach the API — most
        // commonly Vite's index.html fallback for an unmatched route.
        if (typeof body === 'string' && body.startsWith('<!DOCTYPE')) {
          setErrMsg(
            `API call was answered by the SPA fallback (HTML), not the API.\n` +
            `URL called: ${calledUrl}\n` +
            `Content-Type: ${contentType}\n\n` +
            `Likely cause: Vite is proxying / serving from the wrong host.\n` +
            `Fix: ensure VITE_API_BASE_URL=http://localhost:5020 in .env.local AND restart Vite (Ctrl+C, then npm run dev).`
          );
          return;
        }
        const lower = lcKeys(body);                          // normalize all keys → lowerCamel
        const candidate = (lower as { data?: unknown }).data ?? lower;
        const isSettings = (o: unknown): o is EmailAlertSettings =>
          !!o && typeof o === 'object' && 'settingkey' in (o as Record<string, unknown>);
        if (isSettings(candidate)) {
          // Rewrap into the typed shape — keys are already lowercased
          const c = candidate as unknown as Record<string, unknown>;
          setS({
            settingKey:      String(c.settingkey ?? 'DailySummary'),
            enabled:         Boolean(c.enabled),
            fromAddress:     (c.fromaddress as string | null) ?? null,
            toAddresses:     Array.isArray(c.toaddresses) ? c.toaddresses as string[] : [],
            smtpHost:        (c.smtphost as string | null) ?? null,
            smtpPort:        (c.smtpport as number | null) ?? null,
            smtpUser:        (c.smtpuser as string | null) ?? null,
            hasSmtpPassword: Boolean(c.hassmtppassword),
            useSsl:          Boolean(c.usessl),
            sendHourLocal:   Number(c.sendhourlocal ?? 8),
          });
        } else {
          setErrMsg(
            'Server returned an unexpected response shape.  Raw body:\n' +
            JSON.stringify(body, null, 2).slice(0, 800)
          );
        }
      })
      .catch(e => {
        if ((e as { code?: string }).code === 'ERR_CANCELED') return; // timeout already set the message
        const status = (e as { response?: { status?: number } }).response?.status;
        if (status === 401)      setErrMsg('Session expired.  Reloading…'); /* interceptor logs out */
        else if (status === 403) setErrMsg('You do not have permission to view email-alert settings.');
        else setErrMsg(
          (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
          ?? (e as { message?: string }).message
          ?? 'Failed to load.');
      })
      .finally(() => { window.clearTimeout(timer); setLoading(false); });

    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [attempt]);

  /** Reload the live settings from the server — reused by the toolbar Refresh
   *  and by Cancel (to discard in-flight edits).  Bumps `attempt` which re-runs
   *  the guarded fetch effect above. */
  const load = () => { setAttempt(a => a + 1); };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 13 }}>
        <LoaderSymbol size={14} /> Loading settings…
      </div>
    );
  }
  if (!s) {
    return (
      <div style={{ padding: 40, fontSize: 13, maxWidth: 760, margin: '40px auto' }}>
        <div style={{ background: 'var(--ai-pill-alert-bg)', border: '1px solid var(--ai-pill-alert-fg)',
                      color: 'var(--ai-red)', padding: '12px 16px', borderRadius: 4,
                      whiteSpace: 'pre-wrap', fontFamily: 'inherit', textAlign: 'left' }}>
          <i className="ti ti-alert-triangle" />&nbsp;{errMsg ?? 'Settings unavailable.'}
        </div>
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button className="ca-btn" onClick={() => setAttempt(a => a + 1)}>
            <span className="tb-i">🔄</span> Retry
          </button>
        </div>
      </div>
    );
  }

  const update = <K extends keyof EmailAlertSettings>(k: K, v: EmailAlertSettings[K]) =>
    setS(prev => prev ? { ...prev, [k]: v } : prev);

  const addRecipient = () => update('toAddresses', [...(s.toAddresses ?? []), '']);
  const setRecipient = (i: number, v: string) => {
    const arr = [...(s.toAddresses ?? [])];
    arr[i] = v;
    update('toAddresses', arr);
  };
  const delRecipient = (i: number) => {
    const arr = [...(s.toAddresses ?? [])];
    arr.splice(i, 1);
    update('toAddresses', arr);
  };

  /** Persist EVERYTHING through the EXISTING PUT, then re-hydrate from the
   *  response (accept envelope-or-direct, any case) — the same body as before. */
  const persistAll = async () => {
    setErrMsg(null);
    const res = await api.put('/api/v1/settings/email-alerts', {
      enabled:       s.enabled,
      fromAddress:   s.fromAddress,
      toAddresses:   (s.toAddresses ?? []).filter(t => t.trim().length > 0),
      smtpHost:      s.smtpHost,
      smtpPort:      s.smtpPort,
      smtpUser:      s.smtpUser,
      smtpPassword:  pwd,                              // '' means "keep existing"
      useSsl:        s.useSsl,
      sendHourLocal: s.sendHourLocal,
    });
    // Same shape-flex as GET: accept envelope-or-direct, any case.
    const lower = lcKeys(res.data) as Record<string, unknown>;
    const c = ((lower.data as Record<string, unknown> | undefined) ?? lower);
    if (c && 'settingkey' in c) {
      setS({
        settingKey:      String(c.settingkey ?? 'DailySummary'),
        enabled:         Boolean(c.enabled),
        fromAddress:     (c.fromaddress as string | null) ?? null,
        toAddresses:     Array.isArray(c.toaddresses) ? c.toaddresses as string[] : [],
        smtpHost:        (c.smtphost as string | null) ?? null,
        smtpPort:        (c.smtpport as number | null) ?? null,
        smtpUser:        (c.smtpuser as string | null) ?? null,
        hasSmtpPassword: Boolean(c.hassmtppassword),
        useSsl:          Boolean(c.usessl),
        sendHourLocal:   Number(c.sendhourlocal ?? 8),
      });
    }
    setPwd('');
  };

  /** Toolbar Save — shared view-then-edit save (confirm → audited PUT →
   *  "Saved successfully." → back to read-only). Stays on the page (no list
   *  view; the daily worker re-reads within 5 minutes). */
  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'Email Alert Settings',
      successMessage: 'Settings saved successfully. Daily worker will pick up changes within 5 minutes.',
      save: persistAll,
      onError: (e: unknown) => notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message
        ?? (e as { message?: string }).message
        ?? 'Save failed.'),
    }).finally(() => setSaving(false));
  };

  /** Cancel — discard edits by reloading the settings from the server. */
  const handleCancel = () => gate.cancel(() => load());

  // ── Styles — BO UI Standard skeleton: token greys, white cards on --ai-bg ──
  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--ai-ink)',
                                       display: 'block', marginBottom: 4 };
  const input: React.CSSProperties = { padding: '6px 10px', fontSize: 13,
                                       border: '1px solid var(--ai-line)', borderRadius: 4,
                                       width: '100%', maxWidth: 380, boxSizing: 'border-box' };
  const section: React.CSSProperties = { background: '#fff', border: '1px solid var(--ai-line)',
                                         borderRadius: 10, padding: '14px 18px', marginBottom: 14 };
  const sectionTitle: React.CSSProperties = { fontSize: 13, fontWeight: 700, marginBottom: 10,
                                              color: 'var(--ai-ink)' };

  return (
    // Same layout shell as Server Monitor + Server History: take the
    // available height inside the form slot, own the scrollbar, sit
    // edge-to-edge next to the navigation panel.
    <div
      className="ea-scroll"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: 'var(--ai-bg)',
      }}
    >
      <style>{`
        .ea-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
        .ea-scroll::-webkit-scrollbar-track { background: transparent; }
        .ea-scroll::-webkit-scrollbar-thumb { background: var(--ai-line); border-radius: 5px; }
        .ea-scroll::-webkit-scrollbar-thumb:hover { background: var(--ai-muted); }
      `}</style>
    <div style={{ padding: '14px 20px' }}>
      {/* Shared form title — text/icon/colour from the nav entry (navMeta) plus
          the config-gated Audit Trail chip (FormName "Email Alert Settings"). */}
      <FormTitle pageId="emailalerts" auditFormName="Email Alert Settings" />
      <div style={{ fontSize: 12, color: 'var(--ai-muted)', margin: '6px 0 14px' }}>
        Configure the Server Monitor daily summary email.  Changes save to
        the database and take effect within 5 minutes — no API restart needed.
      </div>

      {/* Standard action bar (§18.6): view-then-Edit (shared EditGateActions —
          ✎ Edit → 💾 Save / ↩︎ Cancel) · Refresh.  Read-only until Edit; Save
          persists through the existing PUT in one audited batch. */}
      <div style={{ margin: '0 0 12px' }}>
        <AppToolbar
          left={
            <EditGateActions
              gate={gate}
              saving={saving}
              disabled={loading}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          }
          onRefresh={() => load()}
          refreshing={loading}
          refreshDisabled={gate.editing}
        />
      </div>

      {/* Enable toggle */}
      <div style={section}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={s.enabled} disabled={ro}
                 onChange={e => update('enabled', e.target.checked)}
                 style={{ width: 16, height: 16 }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Enable daily Server Monitor summary email
          </span>
        </label>
        <div style={{ marginLeft: 26, marginTop: 4, fontSize: 11, color: 'var(--ai-muted)' }}>
          When on, every day at the configured hour (local time) the worker
          sends yesterday's Server Monitor summary PDF to all recipients below.
        </div>
      </div>

      {/* From + To */}
      <div style={section}>
        <div style={sectionTitle}>
          <i className="ti ti-at" style={{ marginRight: 6 }} /> Addresses
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={label}>From</label>
          <input style={input} type="email" disabled={ro}
                 value={s.fromAddress ?? ''}
                 onChange={e => update('fromAddress', e.target.value)}
                 placeholder="alpaca-monitor@your-domain.com" />
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: 4 }}>
            <span style={{ ...label, marginBottom: 0 }}>
              To &nbsp;
              <span style={{ fontWeight: 400, color: 'var(--ai-muted)' }}>
                ({s.toAddresses?.length ?? 0} recipient{(s.toAddresses?.length ?? 0) === 1 ? '' : 's'})
              </span>
            </span>
            <button className="ca-btn" onClick={addRecipient} disabled={ro}>
              <span className="tb-i tb-i-green">＋</span> Add recipient
            </button>
          </div>
          {(s.toAddresses ?? []).length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--ai-muted)', padding: '8px 0' }}>
              No recipients yet — click "Add recipient" to add the first one.
            </div>
          )}
          {(s.toAddresses ?? []).map((addr, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <input style={{ ...input, maxWidth: 420 }} type="email" disabled={ro}
                     value={addr}
                     onChange={e => setRecipient(i, e.target.value)}
                     placeholder="someone@example.com" />
              <button className="ca-btn" onClick={() => delRecipient(i)} disabled={ro} title="Remove recipient">
                <span className="tb-i tb-i-red">🗑</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* SMTP */}
      <div style={section}>
        <div style={sectionTitle}>
          <i className="ti ti-server" style={{ marginRight: 6 }} /> SMTP server
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={label}>SMTP host</label>
            <input style={input} type="text" disabled={ro}
                   value={s.smtpHost ?? ''}
                   onChange={e => update('smtpHost', e.target.value)}
                   placeholder="smtp.office365.com" />
          </div>
          <div>
            <label style={label}>Port</label>
            <input style={{ ...input, maxWidth: 100 }} type="number" min={1} max={65535} disabled={ro}
                   value={s.smtpPort ?? ''}
                   onChange={e => update('smtpPort', e.target.value ? parseInt(e.target.value, 10) : null)}
                   placeholder="587" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={label}>Username</label>
            <input style={input} type="text" disabled={ro}
                   value={s.smtpUser ?? ''}
                   onChange={e => update('smtpUser', e.target.value)}
                   placeholder="account@your-domain.com" />
          </div>
          <div>
            <label style={label}>
              Password
              {s.hasSmtpPassword && <span style={{
                marginLeft: 6, fontSize: 10, color: 'var(--ai-green)', fontWeight: 500,
              }}>(saved)</span>}
            </label>
            <input style={input} type="password" disabled={ro}
                   value={pwd}
                   onChange={e => setPwd(e.target.value)}
                   placeholder={s.hasSmtpPassword ? '••••••• (leave blank to keep)' : 'app password'} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={s.useSsl} disabled={ro}
                 onChange={e => update('useSsl', e.target.checked)} />
          Use SSL/TLS (recommended)
        </label>
      </div>

      {/* Schedule */}
      <div style={section}>
        <div style={sectionTitle}>
          <i className="ti ti-clock" style={{ marginRight: 6 }} /> Schedule
        </div>
        <label style={label}>Send hour (local time, 0–23)</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input style={{ ...input, maxWidth: 80 }} type="number" min={0} max={23} disabled={ro}
                 value={s.sendHourLocal}
                 onChange={e => update('sendHourLocal',
                   Math.max(0, Math.min(23, parseInt(e.target.value || '0', 10))))} />
          <span style={{ fontSize: 12, color: 'var(--ai-muted)' }}>
            Daily at <strong>{String(s.sendHourLocal).padStart(2, '0')}:00</strong> server local time
          </span>
        </div>
      </div>

      {/* Save / Cancel live in the toolbar above (shared EditGateActions). */}
      <div style={{ marginTop: 14, fontSize: 11, color: 'var(--ai-muted)' }}>
        The daily email worker re-reads settings every 5 minutes.
      </div>
    </div>
    </div>
  );
}
